import { mkdir, writeFile } from 'node:fs/promises';
import { join } from 'node:path';

import dayjs from 'dayjs';

import env from '#shared/config/env.js';

import appError from './appError.js';
import appDebug from './appDebug.js';
import json from './json.js';
import logger from './logger.js';
import sanitizePath from './sanitizePath.js';

/**
 * Log requests to files and Grafana
 * @param {string} route
 * @param {object} request
 * @param {object} response
 * @param {number} requestStartTime
 * @param {object} session
 * @returns {Promise}
 */
export default async (route, request, response, requestStartTime, session) => {
	const url = IS_SERVER ? new URL(route) : new URL(route, location.origin);
	const fileContent = {
		route,
		request: {
			...request,
			agent: null,
			query: Object.fromEntries(url.searchParams.entries())
		},
		response,
		session
	};
	try {
		if (IS_SERVER) {
			const node = url.pathname.replace(/\/(?:[a-z]{2}-[a-z]{2}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|\d{9,}|\w{3,}\d{9,}|[a-z]{3,}\.\d+\.\d+m)/g, ''); // replace unique parts of the path (locale, payment source GUID, order ID, SKU)
			const sanitizedNode = sanitizePath(node);
			const labels = {
				hostname: url.hostname,
				node,
				method: request.method,
				status: response.status
			};
			logger.count('dependency_status', 1, labels);
			logger.timing('dependency_timing', requestStartTime, labels);
			if (!env.isDev && !env.isStage && !env.isProduction) {
				const dateString = dayjs().format('DD-MM-YYYY HH-mm-ss-SSS');
				const dirPath = join(env.paths.projectPath, 'temp/log', url.hostname);
				const fileName = `${dateString} ${request.method}_${sanitizedNode}.json`;
				const filePath = join(dirPath, fileName);
				await mkdir(dirPath, {
					recursive: true
				});
				await writeFile(filePath, json.stringify(fileContent, null, '\t'));
			}
			// Log requests called from Backend to providers (eStore, MyAccount, Pricing, etc.)
			logger.debug({
				code: 'APP_SHARED_UTIL',
				origin: 'logRequest',
				message: `${fileContent.request.method} ${url.pathname}`,
				detail: {
					session,
					request: {
						method: fileContent.request.method,
						hostname: url.hostname,
						path: url.pathname,
						headers: json.stringify(fileContent.request.headers || {}),
						body: fileContent.request.body || '',
						query: json.stringify(fileContent.request.query || {})
					},
					response: {
						status: fileContent.response.status,
						headers: json.stringify(fileContent.response.headers),
						body: json.stringify(fileContent.response.body)
					}
				}
			});
		}
		if (IS_CLIENT) {
			appDebug('HTTP request', {
				code: 'APP_SHARED_UTIL',
				origin: 'logRequest',
				detail: fileContent
			});
		}
	} catch (error) {
		appError(error, {
			code: 'APP_SHARED_UTIL',
			origin: 'logRequest',
			description: 'Error while logging the request'
		});
	}
};
